import { ArrowRight, Award, Globe2, HandHeart, Shield, Users, BookOpen, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";

const PRES_IMG = "https://customer-assets.emergentagent.com/job_frontuni-rdc/artifacts/74lwn3yb_IMG_1478.png";
const EU_IMG = "https://customer-assets.emergentagent.com/job_frontuni-rdc/artifacts/wjb8wwvi_IMG_1479.png";

const PILLARS = [
  { icon: Shield, t: "Souveraineté & sécurité", d: "Défendre l'intégrité territoriale, dénoncer les agressions étrangères et soutenir les forces de défense nationale." },
  { icon: HandHeart, t: "Solidarité concrète", d: "Mobiliser la diaspora pour les populations civiles, les familles des FARDC et les blessés de guerre." },
  { icon: Award, t: "Justice & mémoire", d: "Documenter les crimes commis contre les civils et plaider auprès des juridictions internationales." },
  { icon: Globe2, t: "Plaidoyer international", d: "Porter la voix du Congo auprès du Parlement européen, des ambassades et des opinions publiques." },
  { icon: Users, t: "Transfert de compétences", d: "Faire de la diaspora une force stratégique au service du développement et de la modernisation de la RDC." },
  { icon: BookOpen, t: "Information & vérité", d: "Lutter contre les discours de haine et restaurer une information fiable sur la réalité congolaise." },
  { icon: Megaphone, t: "Engagement citoyen", d: "Encourager la jeunesse, la culture et la mobilisation patriotique entre toutes les générations." },
];

const PARAGRAPHS = [
  "Le Front Uni pour la RDC est une organisation citoyenne et patriotique née de la volonté de rassembler les Congolaises et Congolais de la diaspora autour d'un idéal commun : défendre la souveraineté, la dignité, l'unité et le développement durable de la République démocratique du Congo.",
  "Fondée en Belgique et active au sein du Benelux ainsi qu'auprès des communautés congolaises francophones à travers le monde, l'association porte une vision profondément engagée en faveur d'un Congo fort, stable, juste et respecté sur la scène internationale.",
  "Notre vision repose sur la conviction que la diaspora congolaise constitue une force stratégique capable de contribuer activement à la transformation du pays par le transfert de compétences, la mobilisation citoyenne, l'engagement intellectuel, le plaidoyer international et la solidarité concrète envers les populations congolaises.",
  "Le Front Uni pour la RDC aspire à construire une plateforme moderne, structurée et inclusive où les Congolais du monde entier peuvent unir leurs compétences, leurs expériences et leurs ressources au service de l'intérêt national. À travers ses actions, l'association souhaite promouvoir une nouvelle génération de leadership responsable, éthique et patriote, capable d'accompagner la RDC face aux défis géopolitiques, économiques, sécuritaires et sociaux du XXIᵉ siècle.",
  "Notre vision s'inscrit également dans une dynamique de justice, de mémoire et de vérité. L'association œuvre pour la défense des droits humains, la lutte contre les discours de haine, la dénonciation des crimes commis contre les populations civiles en RDC, ainsi que la valorisation de l'image des institutions congolaises auprès des opinions publiques internationales.",
  "Le Front Uni pour la RDC croit profondément à la cohésion entre les Congolais du pays et ceux de la diaspora. C'est pourquoi nous encourageons les échanges culturels, éducatifs, économiques, médiatiques et citoyens afin de renforcer les liens entre les différentes générations et communautés congolaises à travers le monde.",
  "À travers ses conférences, ses actions citoyennes, ses projets médiatiques, ses plateformes numériques et ses initiatives de sensibilisation, le Front Uni pour la RDC ambitionne de devenir une référence internationale de mobilisation patriotique, de réflexion stratégique et d'engagement citoyen au service de la République démocratique du Congo.",
];

export default function OrganizationPage() {
  return (
    <div data-testid="organization-page">
      {/* HERO */}
      <section className="bg-ink text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={EU_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="eyebrow eyebrow-on-dark flex items-center gap-3">
            <span className="w-8 h-px bg-gold" /> Notre vision
          </div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight mt-7 leading-[1.02] max-w-5xl">
            Unir les forces, défendre la nation,
            <br /><span className="text-gold italic font-light">préparer l'avenir du Congo.</span>
          </h1>
          <p className="mt-10 text-white/80 max-w-3xl text-lg sm:text-xl leading-relaxed font-serif italic">
            Une organisation citoyenne et patriotique au service de la souveraineté,
            de la dignité et du développement durable de la République démocratique du Congo.
          </p>
        </div>
      </section>

      {/* VISION — text body */}
      <section className="py-24 lg:py-32 bg-white" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow text-gold-dark">L'organisation</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink mt-3 leading-tight tracking-tight">
              Notre vision
            </h2>
            <div className="divider-gold w-20 mt-6" />
            <p className="mt-6 text-foreground/70 text-sm leading-relaxed">
              ASBL fondée en Belgique · active au Benelux et auprès des communautés
              congolaises francophones à travers le monde.
            </p>
            <div className="mt-8 space-y-1 text-[0.85rem]">
              <div className="text-foreground/60 uppercase tracking-[0.18em] text-[0.65rem] font-semibold">Statut</div>
              <div className="text-ink font-serif">Association sans but lucratif</div>
            </div>
            <div className="mt-5 space-y-1 text-[0.85rem]">
              <div className="text-foreground/60 uppercase tracking-[0.18em] text-[0.65rem] font-semibold">Champ d'action</div>
              <div className="text-ink font-serif">International · Diaspora congolaise</div>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {PARAGRAPHS.map((p, i) => (
              <p key={i} className={`leading-[1.85] text-foreground/85 ${i === 0 ? "text-lg font-serif text-ink first-letter:font-serif first-letter:text-5xl first-letter:text-gold-dark first-letter:float-left first-letter:mr-2 first-letter:leading-none" : "text-[1.02rem]"}`}>
                {p}
              </p>
            ))}
            <blockquote className="mt-10 border-l-2 border-gold pl-6 py-2 font-serif text-2xl italic text-ink leading-snug">
              Notre engagement est clair&nbsp;:
              <br /><span className="text-gold-dark">unir les forces, défendre la nation et préparer l'avenir du Congo.</span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* President */}
      <section className="py-24 lg:py-32 bg-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-sm shadow-xl">
              <img src={PRES_IMG} alt="Conférence de presse Hôtel Amigo, Bruxelles" className="w-full h-full object-cover" />
            </div>
            <div className="eyebrow text-gold-dark mt-4 text-center">Conférence de presse · Hôtel Amigo, Bruxelles</div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow text-gold-dark">Présidence</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink tracking-tight mt-3 leading-tight">
              Dr Audrey Mundine Kabeya
            </h2>
            <div className="eyebrow text-foreground/60 mt-2">Présidente du Front Uni pour la RDC</div>
            <div className="divider-gold w-20 mt-6" />
            <p className="mt-6 text-foreground/80 leading-relaxed text-[1.02rem]">
              Sous l'impulsion de la Dr Audrey Mundine Kabeya, le Front Uni porte la voix de la diaspora
              auprès des institutions européennes — Parlement européen, ambassades, Commission européenne —
              et organise des actions humanitaires concrètes auprès des familles des militaires des FARDC et des blessés de guerre.
            </p>
            <blockquote className="mt-8 border-l-2 border-gold pl-6 font-serif text-xl italic text-ink/85 leading-relaxed">
              « C'est pour nous une façon de témoigner notre amour aux militaires qui sont au front,
              pour leur dire que nous sommes derrière eux et que nous sommes fiers des sacrifices qu'ils font pour l'amour du Congo. »
            </blockquote>
          </div>
        </div>
      </section>

      {/* Axes */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="eyebrow text-gold-dark">Nos axes</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink mt-3 leading-tight tracking-tight">
              Sept piliers de mobilisation
            </h2>
            <p className="mt-6 text-foreground/70 leading-relaxed">
              Sept axes structurent notre engagement institutionnel et citoyen au service de la République démocratique du Congo.
            </p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p) => (
              <div key={p.t} className="bg-bone border border-border p-7 rounded-sm hover:border-ink/30 hover:shadow-lg transition-all" data-testid={`pillar-${p.t}`}>
                <p.icon className="w-7 h-7 text-gold-dark" />
                <h3 className="font-serif text-xl text-ink mt-5 leading-snug">{p.t}</h3>
                <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ink text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight">Rejoindre la mobilisation internationale</h2>
          <p className="mt-6 text-white/80 leading-relaxed max-w-2xl mx-auto">
            Que vous soyez membre de la diaspora, partenaire institutionnel ou citoyen engagé,
            rejoignez-nous lors de nos prochains rendez-vous.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/adhesion" className="btn-gold" data-testid="org-cta-adhesion">
              Devenir membre <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/evenements" className="text-white/80 hover:text-gold text-sm font-semibold inline-flex items-center gap-2" data-testid="org-cta-events">
              Voir les événements <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="text-white/80 hover:text-gold text-sm font-semibold inline-flex items-center gap-2" data-testid="org-cta-contact">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
